import {
  ApplicationPipelineStatus,
  createVisitSlots,
  deleteOwnerListing,
  ListingManagerPayload,
  loadOwnerListingManager,
  ListingManagerStatus,
  updateOwnerApplication,
  updateOwnerListingStatus,
  updateVisitRequestStatus,
  updateVisitSlot,
  VisitRequestStatus,
} from 'api/services/ownerListingManagerService'
import NativeDateInput from 'components/common/field/NativeDateInput'
import PageLoading from 'components/common/loading/PageLoading'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  APPLICATION_STATUSES,
  VISIT_REQUEST_STATUSES,
  VISIT_SLOT_STATUSES,
} from 'types/workflowStatus'

type ManagerTab = 'overview' | 'visits'

const TABS: ManagerTab[] = ['overview', 'visits']

function statusBadgeClass(status: string): string {
  if (status === 'active' || status === 'approved' || status === 'confirmed') {
    return 'border border-emerald-300 bg-emerald-100 text-emerald-900'
  }
  if (status === 'paused' || status === 'in_review' || status === 'pending') {
    return 'border border-blue-300 bg-blue-50 text-imovSecondary'
  }
  if (status === 'closed' || status === 'rejected' || status === 'cancelled') {
    return 'border border-red-300 bg-red-100 text-red-900'
  }
  return 'border border-gray-300 bg-gray-100 text-gray-700'
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(status)}`}>
      {label}
    </span>
  )
}

const OwnerListingManagerPage = () => {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const [search, setSearch] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const tabParam = search.get('tab')
  const tab: ManagerTab =
    tabParam === 'applications' || tabParam === 'settings'
      ? 'overview'
      : TABS.includes(tabParam as ManagerTab)
        ? (tabParam as ManagerTab)
        : 'overview'
  const [applicationsFilter, setApplicationsFilter] = useState<string>('all')
  const [slotStart, setSlotStart] = useState('')
  const [slotEnd, setSlotEnd] = useState('')
  const [slotCapacity, setSlotCapacity] = useState('1')
  const [slotNotes, setSlotNotes] = useState('')
  const [repeatWeeks, setRepeatWeeks] = useState('0')

  const statusLabel = (status: string) =>
    t(`account.listingManager.status.${status}`, { defaultValue: status })

  const listingStatusLabel = (status: ListingManagerStatus) =>
    t(`account.listingManager.listingStatus.${status}`, { defaultValue: status })

  const managerQuery = useQuery<ListingManagerPayload>(
    ['ownerListingManager', id],
    () => loadOwnerListingManager(id),
    { enabled: Boolean(id) }
  )

  const updateCache = (data: ListingManagerPayload) => {
    queryClient.setQueryData(['ownerListingManager', id], data)
    queryClient.invalidateQueries(['myProperties'])
  }

  const statusMutation = useMutation(
    (status: ListingManagerStatus) => updateOwnerListingStatus(id, status),
    {
      onSuccess: data => {
        updateCache(data)
        toast.success(String(t('account.listingManager.statusUpdateSuccess')))
      },
      onError: (e: unknown) => {
        toast.error(
          e instanceof Error ? e.message : String(t('account.listingManager.statusUpdateFailed'))
        )
      },
    }
  )

  const deleteListingMutation = useMutation(() => deleteOwnerListing(id), {
    onSuccess: async () => {
      toast.success(String(t('account.listingManager.deleteSuccess')))
      await queryClient.invalidateQueries(['myProperties'])
      navigate('/account')
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : String(t('account.listingManager.statusUpdateFailed')))
    },
  })

  const createSlotMutation = useMutation(createVisitSlots, {
    onSuccess: data => {
      updateCache(data)
      toast.success(String(t('account.listingManager.slotsCreated')))
      setSlotStart('')
      setSlotEnd('')
      setSlotCapacity('1')
      setSlotNotes('')
      setRepeatWeeks('0')
    },
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('account.listingManager.slotsCreateFailed'))
      )
    },
  })

  const updateSlotMutation = useMutation(updateVisitSlot, {
    onSuccess: data => updateCache(data),
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('account.listingManager.slotUpdateFailed'))
      )
    },
  })

  const updateVisitReqMutation = useMutation(updateVisitRequestStatus, {
    onSuccess: data => updateCache(data),
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('account.listingManager.visitUpdateFailed'))
      )
    },
  })

  const updateAppMutation = useMutation(updateOwnerApplication, {
    onSuccess: data => updateCache(data),
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error
          ? e.message
          : String(t('account.listingManager.applicationUpdateFailed'))
      )
    },
  })

  const data = managerQuery.data

  const filteredApplications = useMemo(() => {
    const all = data?.applications ?? []
    if (applicationsFilter === 'all') return all
    return all.filter(a => (a.application_status || 'submitted_to_owner') === applicationsFilter)
  }, [applicationsFilter, data?.applications])

  const handleDeactivate = () => {
    if (!window.confirm(String(t('account.listingManager.confirmDeactivate')))) return
    statusMutation.mutate('paused')
  }

  const handleDelete = () => {
    if (!window.confirm(String(t('account.listingManager.confirmDelete')))) return
    deleteListingMutation.mutate()
  }

  if (managerQuery.isLoading) return <PageLoading minHeightClassName="min-h-[calc(100vh-260px)]" />

  if (!data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <p className="text-sm text-gray-600">{t('account.listingManager.loadError')}</p>
      </div>
    )
  }

  const isPausedOrClosed = data.listing.status === 'paused' || data.listing.status === 'closed'
  const listingDisplayName =
    data.listing.title?.trim() || String(t('account.mySpace.untitledListing'))

  return (
    <div className="min-h-screen bg-imovWhiteShadow py-6 md:py-8">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-4 text-sm text-gray-500">
          <Link to="/account" className="hover:text-imovPrimary">
            {t('account.listingManager.breadcrumbAccount')}
          </Link>
          <span className="mx-2">/</span>
          <span>{t('account.listingManager.listingRef', { id })}</span>
        </div>

        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{t('account.listingManager.title')}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-imovSecondary">{listingDisplayName}</h1>
              <StatusBadge
                status={data.listing.status}
                label={listingStatusLabel(data.listing.status)}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">{t('account.listingManager.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              onClick={() =>
                navigate('/create-ad', {
                  state: { editPropertyId: String(id), returnTo: `/my-listings/${id}/manage` },
                })
              }
            >
              {t('account.listingManager.editListing')}
            </button>
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleDeactivate}
              disabled={statusMutation.isLoading || isPausedOrClosed}
            >
              {t('account.listingManager.deactivateListing')}
            </button>
            <button
              type="button"
              className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleDelete}
              disabled={deleteListingMutation.isLoading}
            >
              {t('account.listingManager.deleteListing')}
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map(tabKey => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setSearch({ tab: tabKey })}
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                tab === tabKey
                  ? 'bg-imovPrimary text-white'
                  : 'border border-gray-200 bg-white text-gray-700'
              }`}
            >
              {t(`account.listingManager.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {(
                [
                  ['favorites', data.metrics.favorites],
                  ['applications', data.metrics.applications],
                  ['visitSlots', data.metrics.visitsSlots],
                  ['visitRequests', data.metrics.visitsRequests],
                  ['contacts', data.metrics.contacts],
                ] as const
              ).map(([key, value]) => (
                <div key={key} className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-xs text-gray-500">{t(`account.listingManager.metrics.${key}`)}</p>
                  <p className="mt-1 text-xl font-bold">{value}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-imovSecondary">
                  {t('account.listingManager.applicationsPipeline')}
                </h2>
                <select
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={applicationsFilter}
                  onChange={e => setApplicationsFilter(e.target.value)}
                >
                  <option value="all">{t('account.listingManager.filterAll')}</option>
                  {APPLICATION_STATUSES.map(s => (
                    <option key={s} value={s}>
                      {statusLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-3 space-y-2">
                {filteredApplications.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('account.listingManager.noApplicationsInFilter')}
                  </p>
                ) : (
                  filteredApplications.map(app => (
                    <div key={app.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {app.applicant_name ||
                              app.applicant_email ||
                              app.applicant_user_fk ||
                              t('account.listingManager.applicationFallback', { id: app.id })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('account.listingManager.moveIn', {
                              date: app.move_in_date || t('account.listingManager.notAvailable'),
                            })}{' '}
                            |{' '}
                            {t('account.listingManager.income', {
                              value: app.income_range || t('account.listingManager.notAvailable'),
                            })}{' '}
                            |{' '}
                            {t('account.listingManager.occupants', {
                              count: app.occupants ?? t('account.listingManager.notAvailable'),
                            })}
                          </p>
                          {app.message ? (
                            <p className="mt-1 text-xs text-gray-700">{app.message}</p>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge
                            status={app.application_status || 'submitted_to_owner'}
                            label={statusLabel(app.application_status || 'submitted_to_owner')}
                          />
                          <select
                            className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                            value={app.application_status || 'submitted_to_owner'}
                            onChange={e =>
                              updateAppMutation.mutate({
                                listingId: id,
                                applicationId: app.id,
                                status: e.target.value as ApplicationPipelineStatus,
                              })
                            }
                          >
                            {APPLICATION_STATUSES.map(s => (
                              <option key={s} value={s}>
                                {statusLabel(s)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-imovSecondary">
                {t('account.listingManager.eventHistory')}
              </h2>
              <div className="mt-3 space-y-2">
                {data.applicationEvents.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('account.listingManager.noEvents')}</p>
                ) : (
                  data.applicationEvents.slice(0, 50).map(evt => (
                    <div key={evt.id} className="rounded-lg border border-gray-200 p-2 text-xs text-gray-700">
                      <p>
                        {t('account.listingManager.eventLine', {
                          date: new Date(evt.created_at).toLocaleString(),
                          type: evt.event_type,
                          from: evt.from_status || '—',
                          to: evt.to_status || '—',
                        })}
                      </p>
                      {evt.message ? <p className="mt-1 text-gray-500">{evt.message}</p> : null}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'visits' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5">
              <h2 className="text-sm font-semibold text-imovSecondary">
                {t('account.listingManager.createVisitSlots')}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {t('account.listingManager.createVisitSlotsHint')}
              </p>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <span
                    id="visit-slot-start-label"
                    className="text-sm font-medium text-gray-800"
                  >
                    {t('account.listingManager.slotStartLabel')}
                  </span>
                  <NativeDateInput
                    type="datetime-local"
                    aria-labelledby="visit-slot-start-label"
                    value={slotStart}
                    onChange={e => setSlotStart(e.target.value)}
                    className="date-input-native h-11 cursor-text rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span id="visit-slot-end-label" className="text-sm font-medium text-gray-800">
                    {t('account.listingManager.slotEndLabel')}
                  </span>
                  <NativeDateInput
                    type="datetime-local"
                    aria-labelledby="visit-slot-end-label"
                    value={slotEnd}
                    onChange={e => setSlotEnd(e.target.value)}
                    className="date-input-native h-11 cursor-text rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-gray-800">
                    {t('account.listingManager.capacity')}
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={slotCapacity}
                    onChange={e => setSlotCapacity(e.target.value)}
                    className="h-11 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    aria-describedby="slot-capacity-help"
                  />
                  <span id="slot-capacity-help" className="text-xs text-gray-500">
                    {t('account.listingManager.capacityHelp')}
                  </span>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-gray-800">
                    {t('account.listingManager.repeatWeeks')}
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={12}
                    value={repeatWeeks}
                    onChange={e => setRepeatWeeks(e.target.value)}
                    className="h-11 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    aria-describedby="slot-repeat-help"
                  />
                  <span id="slot-repeat-help" className="text-xs text-gray-500">
                    {t('account.listingManager.repeatWeeksHelp')}
                  </span>
                </label>
              </div>

              <label className="mt-4 flex flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-800">
                  {t('account.listingManager.notesOptional')}
                </span>
                <input
                  value={slotNotes}
                  onChange={e => setSlotNotes(e.target.value)}
                  className="h-11 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder={t('account.listingManager.notesPlaceholder')}
                />
              </label>

              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  className="rounded-lg bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() =>
                    createSlotMutation.mutate({
                      listingId: id,
                      weeklyRepeatWeeks: Number(repeatWeeks) || 0,
                      slots: [
                        {
                          startAt: slotStart,
                          endAt: slotEnd,
                          capacity: Number(slotCapacity) || 1,
                          notes: slotNotes,
                        },
                      ],
                    })
                  }
                  disabled={createSlotMutation.isLoading || !slotStart || !slotEnd}
                >
                  {t('account.listingManager.create')}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-imovSecondary">
                {t('account.listingManager.slotsTitle')}
              </h2>
              <div className="mt-3 space-y-2">
                {data.visitSlots.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('account.listingManager.noSlots')}</p>
                ) : (
                  data.visitSlots.map(slot => (
                    <div key={slot.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(slot.start_at).toLocaleString()} —{' '}
                            {new Date(slot.end_at).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('account.listingManager.slotCapacity', { count: slot.capacity })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={slot.status} label={statusLabel(slot.status)} />
                          <select
                            className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                            value={slot.status}
                            onChange={e =>
                              updateSlotMutation.mutate({
                                listingId: id,
                                slotId: slot.id,
                                status: e.target.value as (typeof VISIT_SLOT_STATUSES)[number],
                              })
                            }
                          >
                            {VISIT_SLOT_STATUSES.map(s => (
                              <option key={s} value={s}>
                                {statusLabel(s)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-imovSecondary">
                {t('account.listingManager.visitRequestsTitle')}
              </h2>
              <div className="mt-3 space-y-2">
                {data.visitRequests.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('account.listingManager.noVisitRequests')}</p>
                ) : (
                  data.visitRequests.map(req => (
                    <div key={req.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {req.applicant_name ||
                              req.applicant_email ||
                              req.applicant_user_fk ||
                              t('account.listingManager.applicantFallback')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('account.listingManager.slotLabel', {
                              date: req.slot_start_at
                                ? new Date(req.slot_start_at).toLocaleString()
                                : t('account.listingManager.slotNotAvailable'),
                            })}
                          </p>
                          {req.message ? (
                            <p className="mt-1 text-xs text-gray-700">{req.message}</p>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={req.status} label={statusLabel(req.status)} />
                          <select
                            className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                            value={req.status}
                            onChange={e =>
                              updateVisitReqMutation.mutate({
                                listingId: id,
                                requestId: req.id,
                                status: e.target.value as VisitRequestStatus,
                              })
                            }
                          >
                            {VISIT_REQUEST_STATUSES.map(s => (
                              <option key={s} value={s}>
                                {statusLabel(s)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default OwnerListingManagerPage
